import { useContext, useEffect, useRef, useState, Fragment } from "react";
import ReportModal from "./modal/ReportModal";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { SocketContext } from "../../context/SocketContext";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import SkeletonOne from "../../components/skeleton/SkeletonOne";
import { muteUnmuteMessageChat, reportMessage } from "../../redux/ApiSlice/MessagesSlice";

const MessageList = ({ searchQuery = "", searchResults = [], searchLoading = false }) => {
    const menuRef = useRef(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [openMenuId, setOpenMenuId] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportUserId, setReportUserId] = useState(null);
    const [reportChatRoomId, setReportChatRoomId] = useState(null);

    // Get chatRoomList from socket context if available, else fallback to redux
    const { chatRoomList: chatRoomListSocket } = useContext(SocketContext);
    const { loading, chatRoomList: chatRoomListRedux } = useSelector((state) => state.messages);

    const sentinelRef = useRef(null);
    const { fetchMore, hasMore, isFetchingMore } = chatRoomListSocket || {};

    useOutsideClick(menuRef, () => setOpenMenuId(null));

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        // Find the nearest scrollable ancestor to use as the IntersectionObserver root
        let scrollParent = sentinel.parentElement;
        while (scrollParent && scrollParent !== document.body) {
            const { overflow, overflowY } = window.getComputedStyle(scrollParent);
            if (overflow === 'scroll' || overflow === 'auto' || overflowY === 'scroll' || overflowY === 'auto') break;
            scrollParent = scrollParent.parentElement;
        }
        const root = scrollParent !== document.body ? scrollParent : null;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isFetchingMore) {
                    fetchMore?.();
                }
            },
            { root, threshold: 0.1 }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, isFetchingMore, fetchMore]);

    // Prefer socket data if connected, else fallback to redux
    const chatRooms = Array.isArray(chatRoomListSocket?.data) && chatRoomListSocket.isConnected
        ? chatRoomListSocket.data
        : (Array.isArray(chatRoomListRedux) ? chatRoomListRedux : chatRoomListRedux?.chat_room_detail_list || []);

    // Use API search results when query is active, otherwise show all chat rooms
    const filteredChatRooms = searchQuery.trim() ? searchResults : chatRooms;

    const handleMuteUser = (roomId) => {
        dispatch(muteUnmuteMessageChat(roomId));
        setOpenMenuId(null);
    }

    return (
        <Fragment>
            {(loading || searchLoading) ? (
                <SkeletonOne />
            ) : filteredChatRooms.length > 0 ? (
                filteredChatRooms.map((room) => {
                    const user = room.users[0];
                    return (
                        <div
                            key={room.id}
                            className="mx-2 py-1.5 bg-white dark:bg-[#1E1D21] border-b border-gray-300/50 dark:border-gray-800/50 relative"
                        >
                            <div className="flex flex-row w-full items-center">
                                <div className="flex-shrink-0 w-14 h-11 flex items-center relative">
                                    <img
                                        alt="img"
                                        src={user.avatar_url}
                                        onClick={() => navigate(`/message/${room.id}`, { state: { from: user } })}
                                        className="h-11 w-11 rounded-full shadow-2xl object-cover border border-gray-300/50 dark:border-[#333333] cursor-pointer"
                                    />
                                    {/* Unread messages badge */}
                                    {room.unread_messages > 0 && (
                                        <span className="absolute right-2 top-0 bg-red-600 text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1 shadow">
                                            {room.unread_messages}
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-col flex-1 min-w-0">
                                    <div className="flex items-start justify-between">
                                        <p
                                            onClick={() => navigate(`/message/${room.id}`, { state: { from: user } })}
                                            className="font-semibold text-[13px] cursor-pointer text-black dark:text-white truncate flex-1 mr-2">
                                            {user.full_name}
                                        </p>
                                        {/* <div className="flex flex-col items-end relative flex-shrink-0">
                                            <BiDotsHorizontalRounded
                                                className="absolute top-5 right-0 text-[20px] cursor-pointer text-gray-500 dark:text-gray-700"
                                                onClick={() => setOpenMenuId(room.id)}
                                            />
                                            {openMenuId === room.id && (
                                                <div
                                                    ref={menuRef}
                                                    className="absolute text-black dark:text-white bg-white dark:bg-gray-900 border-2 border-indigo-100 -top-1 right-0 rounded z-10"
                                                >
                                                    <p
                                                        onClick={() => handleMuteUser(room.id)}
                                                        className="text-[10px] px-2.5 py-0.5 border-b border-gray-300 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                                    >
                                                        {room.is_muted ? "Unmute" : "Mute"}
                                                    </p>
                                                    <p className="text-[10px] px-2.5 py-0.5 border-b border-gray-300 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                                        onClick={() => {
                                                            onReportUser(user.id, room.id);
                                                            setOpenMenuId(null);
                                                        }}
                                                    >
                                                        Report
                                                    </p>
                                                    <p
                                                        onClick={() => {
                                                            onBlockUser(user.id, `${user.full_name}`);
                                                            setOpenMenuId(null);
                                                        }}
                                                        className="text-[10px] px-2.5 py-0.5 border-b border-gray-300 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                                    >
                                                        Block
                                                    </p>
                                                    <p
                                                        className="text-[10px] px-2.5 py-0.5 border-none hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                                        onClick={() => {
                                                            onDeleteChat(room.id, `${user.full_name}`);
                                                            setOpenMenuId(null);
                                                        }}
                                                    >
                                                        Delete
                                                    </p>
                                                </div>
                                            )} 
                                        </div>*/}
                                    </div>
                                    <div className="flex items-center justify-between gap-x-2">
                                        <p
                                            onClick={() => navigate(`/message/${room.id}`, { state: { from: user } })}
                                            className="text-[12px] line-clamp-1 text-gray-700 dark:text-gray-400 truncate pr-8"
                                        >
                                            {room.latest_message?.message || "No message yet."}
                                        </p>
                                        <p
                                            onClick={() => navigate(`/message/${room.id}`, { state: { from: user } })}
                                            className="text-[10px] line-clamp-1 text-gray-500 dark:text-gray-500"
                                        >
                                            {room.message_time}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })
            ) : (<>
                <p className="text-[12px] text-center mt-20 text-gray-600 dark:text-gray-700">
                    {searchQuery.trim() ? "No matching conversation found.." : "No Messages Found"}
                </p>
            </>
            )}

            {/* Sentinel for cursor pagination — triggers fetchMore when scrolled into view */}
            <div ref={sentinelRef} className="h-1" />
            {isFetchingMore && (
                <div className="flex justify-center items-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                </div>
            )}
            <ReportModal
                isOpen={showReportModal}
                onClose={() => setShowReportModal(false)}
                reportedUserId={reportUserId}
                chatRoomId={reportChatRoomId}
                onReport={async (data) => {
                    await dispatch(reportMessage(data));
                    setShowReportModal(false);
                }}
            />
        </Fragment>
    );
};

export default MessageList;